/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DocumentNode,
  LazyQueryHookOptions,
  MutationHookOptions,
  OperationVariables,
  TypedDocumentNode,
  gql,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import useInfinityDataSource, { ListInfo } from 'src/hooks/useInfinityDataSource';
import buildInfinityHandler from 'src/utils/functions/buildInfinityHandler';
import handleError from 'src/utils/functions/handleError';
import getKeyValue from 'lodash/get';

type OpSpec = {
  op: DocumentNode | TypedDocumentNode<any, OperationVariables>;
  dataPath?: string;
};

type QuerySpec = OpSpec & {
  options?: LazyQueryHookOptions;
};

type MutationSpec = OpSpec & {
  options?: MutationHookOptions;
};

type FilterParam = {
  dataType: string;
  fieldName: string;
  operator: string;
  value: string;
};

type OrderParam = {
  field: string;
  sort: string;
};

type SearchFields = Record<string, string | number | boolean | null | string[]>;

export type AdapterFunc<TInput = any, TOutput = any> = (input: TInput) => TOutput;

export type OperationSpecs = {
  fetchAll: QuerySpec;
  fetchOne: QuerySpec;
  createOne: MutationSpec;
  updateOne: MutationSpec;
  deleteOne: MutationSpec;
  deleteInBulk?: MutationSpec;
};

type AdapterSpecs = {
  entityAdapter: AdapterFunc;
};

type ErrorHandlerFunc = typeof handleError;

type EntityAdapterParams = {
  entityName: string;
  operations: OperationSpecs;
  adapters: AdapterSpecs;
  isPaginated?: boolean;
  hasFilter?: boolean;
  hasSort?: boolean;
  errorHandler?: ErrorHandlerFunc;
};

const PLACEHOLDER_MUTATION = gql`
  mutation {
    FakeMutation {
      fakeMutation
    }
  }
`;

const useEntityAdapter = <TModel = any>({
  isPaginated,
  hasFilter = true,
  hasSort = true,
  operations,
  adapters: { entityAdapter },
  errorHandler = handleError,
}: EntityAdapterParams) => {
  const [fetchAll, { loading: fetchAllLoading }] = useLazyQuery(operations.fetchAll.op, {
    fetchPolicy: 'no-cache',
    ...operations.fetchAll.options,
  });
  const [fetchOne, { loading: fetchOneLoading }] = useLazyQuery(
    operations.fetchOne.op,
    operations.fetchOne.options,
  );
  const [createOne, { loading: createOneLoading }] = useMutation(
    operations.createOne.op,
    operations.createOne.options,
  );
  const [updateOne, { loading: updateOneLoading }] = useMutation(
    operations.updateOne.op,
    operations.updateOne.options,
  );
  const [deleteSingle, { loading: deleteOneLoading }] = useMutation(
    operations.deleteOne.op,
    operations.deleteOne.options,
  );
  const [deleteInBulk] = useMutation(
    operations.deleteInBulk?.op || PLACEHOLDER_MUTATION,
    operations.deleteInBulk?.options,
  );
  const [filters, setFilters] = useState<FilterParam[]>([]);
  const [orderBy, setOrderBy] = useState<OrderParam[]>([]);
  const [searchFields, setSearchFields] = useState<SearchFields>({});

  const getPayload = (data: any) => {
    if (operations.fetchAll?.dataPath) {
      return getKeyValue(data, operations.fetchAll.dataPath);
    }

    return data;
  };

  const handleFetchAll = buildInfinityHandler({
    isPaginated,
    fetchAll,
    entityAdapter,
    errorHandler,
    getOptions(from, size) {
      const includeFilters = hasFilter && filters?.length > 0;
      const includeSort = hasSort && orderBy?.length > 0;
      const options: LazyQueryHookOptions = {};
      if (isPaginated) {
        options.variables = {
          criteria: {
            pagination: { from, size },
            filter: includeFilters ? filters : undefined,
            orderBy: includeSort ? orderBy : undefined,
          },
          searchFields,
        };
      }
      return options;
    },
    getPayload,
  });

  const { dataSource, reset, onListRender, setDataSource } = useInfinityDataSource(handleFetchAll);
  const [selected, setSelected] = useState<TModel | null>(null);

  const loading = useMemo(
    () =>
      fetchAllLoading ||
      fetchOneLoading ||
      createOneLoading ||
      updateOneLoading ||
      deleteOneLoading,
    [fetchAllLoading, fetchOneLoading, createOneLoading, updateOneLoading, deleteOneLoading],
  );

  useEffect(() => {
    reset(dataSource.length);
  }, [filters, searchFields, orderBy]);

  const loadById = async (id: string | number) => {
    try {
      const { data, error } = await fetchOne({ variables: { id } });
      if (error) throw error;
      let entityData = data;
      if (operations.fetchOne.dataPath) {
        entityData = getKeyValue(data, operations.fetchOne.dataPath);
      }
      setSelected(entityAdapter(entityData));
    } catch (error) {
      errorHandler(error);
    }
  };

  const deleteBulk = async (ids: number[]) => {
    if (operations.deleteInBulk) {
      try {
        const { errors } = await deleteInBulk({ variables: { ids } });
        const error: any = errors?.[0] || errors;
        if (error) throw error;

        reset(dataSource.length - ids.length);
      } catch (error) {
        errorHandler(error);
      }
    } else {
      console.warn('deleteInBulk operation is not defined');
    }
  };

  const deleteOne = async (id: number) => {
    try {
      const { errors } = await deleteSingle({ variables: { id } });
      const error: any = errors?.[0] || errors;
      if (error) throw error;

      setDataSource((prev) => prev.filter((data) => id !== data.id));
      return true;
    } catch (error) {
      errorHandler(error);

      return false;
    }
  };

  const adaptedFetchAll = (options?: Partial<LazyQueryHookOptions<any, OperationVariables>>) =>
    fetchAll(options).then((res) => {
      const data = getPayload(res.data);

      if (data.error || data.errors) {
        throw data.error || data.errors;
      }

      if (isPaginated) {
        return {
          ...data,
          results: data.results.map(entityAdapter),
        };
      }

      return data.map(entityAdapter);
    });

  const loadPage = (listInfo = { renderLen: 100, start: 1 } as ListInfo) => {
    onListRender(listInfo);
  };

  return {
    dataSource,
    loading,
    selected,
    filters,
    orderBy,
    setOrderBy,
    searchFields,
    setSearchFields,
    reset,
    loadPage,
    fetchAll: adaptedFetchAll,
    loadById,
    createOne,
    updateOne,
    deleteOne,
    setSelected,
    setFilters,
    deleteBulk,
    setDataSource,
  };
};

export default useEntityAdapter;
